import React, { useState, useEffect } from "react";
import {
    Modal,
    Form,
    Input,
    Select,
    Upload,
    Col,
    Button,
    Card,
    Tooltip,
    Row,
    DatePicker,
    message,
} from "antd";
import {
    UploadOutlined,
    CloseOutlined,
    EyeOutlined,
} from "@ant-design/icons";
import SuccessModal from "../Events/SuccessModal";
import api from "../../../api";
import dayjs from "dayjs";

const { TextArea } = Input;

const CreateNews = ({ visible, handleCancel, handleSubmit, editdata, onSuccess }) => {
    const [form] = Form.useForm();
    const [bannerPreview, setBannerPreview] = useState(null);
    const [bannerFileList, setBannerFileList] = useState([]);
    const [bannerModalVisible, setBannerModalVisible] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isBannerChanged, setIsBannerChanged] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);

    const [messageApi, contextHolder] = message.useMessage();

    const getBase64 = (file) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
        });


    const validateFile = (file) => {
        const allowedTypes = [
            "image/jpeg",
            "image/jpg",
            "image/png",
        ];

        const isValidType = allowedTypes.includes(file.type);

        if (!isValidType) {
            messageApi.error(
                "Only JPG, JPEG and PNG image formats are allowed."
            );
            return Upload.LIST_IGNORE;
        }

        const isValidSize = file.size / 1024 / 1024 <= 5;

        if (!isValidSize) {
            messageApi.error(
                "Image size should not exceed 5 MB."
            );
            return Upload.LIST_IGNORE;
        }

        return true;
    };

    const validateImageDimensions = (file) => {
        return new Promise((resolve, reject) => {
            const img = new Image();

            img.onload = () => {


                // if (img.width >= 880 && img.width <= 940 || img.height >= 1500 && img.height <= 1700) {
                //     messageApi.error(
                //         "Image size must be 768 × 768 pixels"
                //     );
                //     reject(false);
                //     return;
                // }
                resolve(true); // Allow all image sizes
            };

            img.onerror = () => {
                messageApi.error("Invalid image file");
                reject(false);
            };

            img.src = URL.createObjectURL(file);
        });
    };

    const handleUploadChange = ({ fileList }) => {
        const file = fileList[0];

        if (file) {
            getBase64(file.originFileObj).then(setBannerPreview);
            setBannerFileList([file]);
            setIsBannerChanged(true); // ✅ important
        } else {
            setBannerPreview(null);
            setBannerFileList([]);
            setIsBannerChanged(false);
        }
    };

    const handleCloseModal = () => {
        setShowSuccessModal(false);
        form.resetFields();
        setBannerPreview(null);
        setBannerFileList([]);
        setIsBannerChanged(false);
        handleCancel();
    };

    const handleRemoveBanner = () => {
        setBannerPreview(null);
        setBannerFileList([]);
    };

    useEffect(() => {
        if (editdata) {
            form.setFieldsValue({
                newsType: editdata.fld_news_type_name,
                title: editdata.fld_news_title,
                subdescription: editdata.fld_news_sub_description,
                description: editdata.fld_news_description,
                link: editdata.fld_hyper_link,

                publishDate: editdata.fld_news_created_date
                    ? dayjs(editdata.fld_news_created_date, "MM/DD/YYYY")
                    : dayjs(),

                startDate: editdata.fld_form_start_time
                    ? dayjs(editdata.fld_form_start_time, "MM/DD/YYYY")
                    : null,

                endDate: editdata.fld_form_end_time
                    ? dayjs(editdata.fld_form_end_time, "MM/DD/YYYY")
                    : null,

                status:
                    editdata.fld_news_active_id === "1"
                        ? "Active"
                        : "Inactive",
            });

            if (editdata.fld_news_image) {
                setBannerPreview(editdata.fld_news_image);
                setBannerFileList([
                    {
                        uid: "-1",
                        name: "news.png",
                        status: "done",
                        url: editdata.fld_news_image,
                    },
                ]);
                setIsBannerChanged(false);
            }
        } else {
            form.resetFields();
            setBannerPreview(null);
            setBannerFileList([]);
        }
    }, [editdata]);

    const disabledStartDate = (current) =>
        current && current < dayjs().startOf("day");

    const disabledEndDate = (current) => {
        const startDate = form.getFieldValue("startDate");
        return (
            current &&
            (current < dayjs().startOf("day") ||
                (startDate && current < startDate))
        );
    };


    const handleFinish = async (values) => {
        const wasEditing = !!editdata;
        try {
            setLoading(true);

            if (!isBannerChanged && !editdata?.fld_news_image) {
                messageApi.error("Please upload a news image!");
                return;
            }

            const formData = new FormData();

            // ✅ IDs
            formData.append("fld_rf_id", editdata?.fld_rf_id || "");
            formData.append("fld_news_sw_id", editdata?.fld_news_sw_id || ""); // ✅ NEW
            formData.append("fld_news_id", editdata?.fld_news_id || "");

            // ✅ News Type
            const typeId = values.newsType === "General" ? "1" : "2";

            formData.append("fld_news_type_id", typeId);
            formData.append("fld_news_type_name", values.newsType);

            // ✅ Basic Fields
            formData.append("fld_news_title", values.title);
            formData.append("fld_news_description", values.description);
            formData.append("fld_news_sub_description", values.subdescription);
            formData.append(
                "fld_form_start_time",
                values.startDate
                    ? values.startDate.format("MM/DD/YYYY")
                    : ""
            );

            formData.append(
                "fld_form_end_time",
                values.endDate
                    ? values.endDate.format("MM/DD/YYYY")
                    : ""
            );

            formData.append("fld_hyper_link", values.link || "");

            formData.append(
                "fld_news_created_date",
                values.publishDate.format("MM/DD/YYYY")
            );

            // ✅ STATUS (IMPORTANT FIX)
            const isActive = values.status === "Active" ? "1" : "0";

            formData.append("fld_news_active_id", isActive);

            formData.append(
                "fld_news_active_name",
                isActive === "1" ? "active" : "inactive"
            );

            // ❌ REMOVE THIS (wrong field)
            // formData.append("fld_is_active", ...)

            // ✅ Image
            if (isBannerChanged && bannerFileList[0]?.originFileObj) {
                formData.append("fld_image_path", bannerFileList[0].originFileObj);
            } else if (!isBannerChanged && editdata?.fld_news_image) {
                formData.append("fld_image_path", editdata.fld_news_image);
            }

            // ✅ Meta
            formData.append("synceddatetime", "2025-05-29 10:08:00");
            formData.append("FormCode", "102");
            formData.append("ApiKey", "kavin");
            formData.append("AppTypeNo", "3");
            formData.append("AppVersion", "1.0.0.0");

            formData.append("fld_loggedin_user_id", "admin");

            const response = await api.newsCreation(formData);

            if (response?.data?.status === "1") {
                setIsEditMode(wasEditing);
                setShowSuccessModal(true);
                onSuccess?.();
            } else {
                messageApi.error(response?.data?.message || "Failed to create news");
            }
        } catch (error) {
            console.error("❌ Full Error:", error);
            console.error("❌ Response:", error?.response);
            console.error("❌ Response Data:", error?.response?.data);

            messageApi.error(
                error?.response?.data?.message ||
                error?.message ||
                "Something went wrong"
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (visible) {
            if (editdata) {
                form.setFieldsValue({
                    newsType: editdata.fld_news_type_name,
                    title: editdata.fld_news_title,
                    subdescription: editdata.fld_news_sub_description,
                    description: editdata.fld_news_description,
                    link: editdata.fld_hyper_link,

                    publishDate: editdata.fld_news_created_date
                        ? dayjs(editdata.fld_news_created_date, "MM/DD/YYYY")
                        : dayjs(),

                    status:
                        editdata.fld_news_active_id === "1"
                            ? "Active"
                            : "Inactive",
                });
            } else {
                form.resetFields();

                form.setFieldsValue({
                    publishDate: dayjs(), // ✅ auto populate current date
                    status: "Active",
                });

                setBannerPreview(null);
                setBannerFileList([]);
                setIsBannerChanged(false);
            }
        }
    }, [visible, editdata, form]);

    return (
        <>
            {contextHolder}

            <Modal
                open={visible}
                onCancel={handleCancel}
                footer={null}
                width={1000}
                centered
                closeIcon={<CloseOutlined />}
                style={{
                    top: 20, // top margin
                    paddingBottom: 20, // bottom spacing
                }}

                styles={{
                    body: {
                        padding: 24,
                    },
                    mask: {
                        backdropFilter: "blur(8px)", // 🔥 background blur
                        background: "rgba(0,0,0,0.4)", // optional dark overlay
                    },
                }}
            >
                <Card>
                    <h2 style={{ marginBottom: 4 }}> {editdata ? "Edit News" : "Create News"}</h2>
                    <p style={{ color: "#888" }}>
                        {editdata
                            ? "Update the details of your news article."
                            : "Fill in the details to publish a news article."}
                    </p>

                    <Form layout="vertical" form={form} onFinish={handleFinish}>
                        {/* News Type */}
                        <Form.Item
                            name="newsType"
                            label="News Type"
                            rules={[{ required: true }]}
                        >
                            <Select placeholder="Select news type">
                                <Select.Option value="KOPA News">KOPA News</Select.Option>
                                <Select.Option value="Global News">Global News</Select.Option>
                                <Select.Option value="Ad News">Ad News</Select.Option>
                            </Select>
                        </Form.Item>

                        {/* Banner Upload */}
                        <Form.Item
                            label="News Banner"
                            required
                            rules={[{ required: true }]}
                        >
                            <div
                                style={{
                                    height: 200,
                                    border: "1px dashed #d9d9d9",
                                    borderRadius: 6,
                                    overflow: "hidden",
                                    position: "relative",
                                }}
                            >
                                {bannerPreview ? (
                                    <>
                                        <img
                                            src={bannerPreview}
                                            alt="preview"
                                            style={{
                                                width: "100%",
                                                height: "100%",
                                                objectFit: "cover",
                                            }}
                                        />

                                        {/* Overlay Buttons */}
                                        <div
                                            style={{
                                                position: "absolute",
                                                top: 8,
                                                right: 8,
                                                display: "flex",
                                                gap: 8,
                                            }}
                                        >
                                            <Tooltip title="Preview">
                                                <Button
                                                    shape="circle"
                                                    icon={<EyeOutlined />}
                                                    onClick={() => setBannerModalVisible(true)}
                                                />
                                            </Tooltip>

                                            <Tooltip title="Remove">
                                                <Button
                                                    danger
                                                    shape="circle"
                                                    icon={<CloseOutlined />}
                                                    onClick={handleRemoveBanner}
                                                />
                                            </Tooltip>
                                        </div>
                                    </>
                                ) : (
                                    <Upload.Dragger
                                        beforeUpload={async (file) => {
                                            const isValid = validateFile(file);

                                            if (isValid !== true) {
                                                return Upload.LIST_IGNORE;
                                            }

                                            try {
                                                await validateImageDimensions(file);

                                                getBase64(file).then(setBannerPreview);

                                                setBannerFileList([
                                                    {
                                                        uid: file.uid,
                                                        name: file.name,
                                                        originFileObj: file,
                                                    },
                                                ]);

                                                setIsBannerChanged(true);

                                                return false;
                                            } catch {
                                                return Upload.LIST_IGNORE;
                                            }
                                        }}
                                        onChange={handleUploadChange}
                                        fileList={bannerFileList}
                                        maxCount={1}
                                        showUploadList={false}
                                        style={{ height: "100%", border: "none" }}
                                    >
                                        <UploadOutlined style={{ fontSize: 24 }} />

                                        <p>Upload News Banner</p>

                                        <p style={{ color: "#888", fontSize: 12 }}>
                                            Supported formats: JPG, JPEG, PNG
                                        </p>

                                        <p style={{ color: "#888", fontSize: 12 }}>
                                            Maximum file size: 5 MB
                                        </p>
                                    </Upload.Dragger>

                                )}
                            </div>
                        </Form.Item>

                        {/* Title */}
                        <Form.Item
                            name="title"
                            label="News Title"
                            rules={[
                                { required: true, message: "Please enter title" },
                                { min: 5, message: "Minimum 5 characters" },
                                { max: 100, message: "Maximum 100 characters" },
                            ]}
                        >
                            <Input placeholder="Enter title" />
                        </Form.Item>

                        {/* Sub Description */}
                        <Form.Item
                            name="subdescription"
                            label="Short News Description"
                            rules={[
                                { required: true, message: "Please enter sub description" },
                                { min: 5, message: "Minimum 5 characters" },
                                { max: 500, message: "Maximum 500 characters" },
                            ]}
                        >
                            <TextArea placeholder="Enter Sub description" rows={4} maxLength={500} showCount />
                        </Form.Item>

                        {/* Description */}
                        <Form.Item
                            name="description"
                            label="Long News Description"
                            rules={[
                                { required: true, message: "Please enter description" },
                                { min: 5, message: "Minimum 5 characters" },
                                { max: 2000, message: "Maximum 2000 characters" },
                            ]}
                        >
                            <TextArea placeholder="Enter description" rows={4} maxLength={2000} showCount />
                        </Form.Item>

                        {/* Link */}
                        <Form.Item
                            name="link"
                            label="Hyperlink"
                            rules={[
                                { type: "url", message: "Enter a valid URL (https://...)" },
                            ]}
                        >
                            <Input placeholder="https://example.com" />
                        </Form.Item>





                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="publishDate"
                                    label="Publish Date"
                                    rules={[{ required: true, message: "Publish date is required" }]}
                                >
                                    <DatePicker
                                        style={{ width: "100%" }}
                                        disabled
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item name="endDate" label="End Date">
                                    <DatePicker
                                        style={{ width: "100%" }}
                                        disabledDate={(current) => {
                                            const start = form.getFieldValue("startDate");
                                            return (
                                                current &&
                                                (current < dayjs().startOf("day") ||
                                                    (start && current < start))
                                            );
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item
                            name="status"
                            label="Status"
                            rules={[{ required: true, message: "Please select status" }]}
                        >
                            <Select>
                                <Select.Option value="Active">Active</Select.Option>
                                <Select.Option value="Inactive">Inactive</Select.Option>
                            </Select>
                        </Form.Item>

                        {/* Submit */}
                        <Form.Item >
                            <Row justify="end">
                                <div style={{ display: "flex", gap: "30px" }}>
                                    <Button
                                        onClick={handleCancel}
                                        style={{
                                            width: 150,
                                            height: 40,
                                        }}
                                    >
                                        Cancel
                                    </Button>

                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        style={{
                                            backgroundColor: "#1980B9",
                                            borderColor: "#1980B9",
                                            width: 150,
                                            height: 40,
                                        }}
                                        loading={loading}
                                    >
                                        {editdata ? "Update News" : "Publish News"}
                                    </Button>
                                </div>
                            </Row>
                        </Form.Item>
                    </Form>
                </Card>
            </Modal>

            {/* Image Preview Modal */}
            <Modal
                open={bannerModalVisible}
                footer={null}
                onCancel={() => setBannerModalVisible(false)}
            >
                <img src={bannerPreview} alt="preview" style={{ width: "100%" }} />
            </Modal>

            {showSuccessModal === true && (
                <SuccessModal
                    visible={showSuccessModal}
                    onClose={handleCloseModal}
                    title={
                        isEditMode
                            ? "News Updated Successfully"
                            : "News Created Successfully"
                    }
                    description={
                        isEditMode
                            ? "Your news article has been updated successfully."
                            : "Your news article has been created and published."
                    }
                />
            )}
        </>
    );
};

export default CreateNews;